import type { BaseSyntheticEvent } from 'react';
import { useState, useEffect } from 'react';
import { useForm, type UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { useUploadLogo } from '../hooks/useUploadLogo';
import { useUpdateCompany } from '../hooks/useUpdateCompany';
import { updateCompanySchema, type UpdateCompanyFormData } from '../schemas/company.schemas';
import type { Company } from '../types/company.types';
import { toFormValues } from './EditCompanyDialog.utils';

interface HandleLogoUploadsParams {
  companyId: string;
  logoFile: File | null;
  darkLogoFile: File | null;
  uploadLogoAsync: (params: { companyId: string; file: File }) => Promise<unknown>;
  uploadDarkLogoAsync: (params: { companyId: string; file: File }) => Promise<unknown>;
}

export async function handleLogoUploads({
  companyId,
  logoFile,
  darkLogoFile,
  uploadLogoAsync,
  uploadDarkLogoAsync,
}: HandleLogoUploadsParams): Promise<void> {
  const uploads: Promise<unknown>[] = [];
  if (logoFile) uploads.push(uploadLogoAsync({ companyId, file: logoFile }));
  if (darkLogoFile) uploads.push(uploadDarkLogoAsync({ companyId, file: darkLogoFile }));
  await Promise.all(uploads);
}

export function useEditCompanyHandlers(onOpenChange: (open: boolean) => void): {
  isUploading: boolean;
  uploadLogoAsync: ReturnType<typeof useUploadLogo>['uploadLogoAsync'];
  uploadDarkLogoAsync: ReturnType<typeof useUploadLogo>['uploadDarkLogoAsync'];
  onSuccess: (companyId: string, logoFile: File | null, darkLogoFile: File | null) => Promise<void>;
} {
  const { uploadLogoAsync, uploadDarkLogoAsync, isUploading } = useUploadLogo();

  return {
    isUploading,
    uploadLogoAsync,
    uploadDarkLogoAsync,
    onSuccess: async (
      companyId: string,
      logoFile: File | null,
      darkLogoFile: File | null
    ): Promise<void> => {
      try {
        await handleLogoUploads({
          companyId,
          logoFile,
          darkLogoFile,
          uploadLogoAsync,
          uploadDarkLogoAsync,
        });
        toast.success('Company updated successfully');
        onOpenChange(false);
      } catch {
        toast.error('Failed to upload logo');
      }
    },
  };
}

interface EditCompanyForm {
  form: UseFormReturn<UpdateCompanyFormData>;
  onSubmit: (e?: BaseSyntheticEvent) => Promise<void>;
  isLoading: boolean;
  isUploading: boolean;
  logoFile: File | null;
  darkLogoFile: File | null;
  setLogoFile: (file: File | null) => void;
  setDarkLogoFile: (file: File | null) => void;
}

export function useEditCompanyForm(
  company: Company,
  open: boolean,
  onOpenChange: (open: boolean) => void
): EditCompanyForm {
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [darkLogoFile, setDarkLogoFile] = useState<File | null>(null);
  const { updateCompany, isLoading } = useUpdateCompany();
  const handlers = useEditCompanyHandlers(onOpenChange);
  const form = useForm<UpdateCompanyFormData>({
    resolver: zodResolver(updateCompanySchema),
    defaultValues: toFormValues(company),
  });

  useEffect(() => {
    if (open) {
      form.reset(toFormValues(company));
      setLogoFile(null);
      setDarkLogoFile(null);
    }
  }, [open, company, form]);

  const onSubmit = async (data: UpdateCompanyFormData): Promise<void> => {
    updateCompany(
      { id: company.id, data: { ...data, country: data.country ?? '' } },
      {
        onSuccess: () => handlers.onSuccess(company.id, logoFile, darkLogoFile),
        onError: () => toast.error('Failed to update company'),
      }
    );
  };

  return {
    form,
    onSubmit: form.handleSubmit(onSubmit),
    isLoading,
    isUploading: handlers.isUploading,
    logoFile,
    darkLogoFile,
    setLogoFile,
    setDarkLogoFile,
  };
}
